/**
 * As implementacoes de navegador das portas de `purga.ts`.
 *
 * Este e o **unico** arquivo do modulo de armazenamento que toca `indexedDB`, `caches`,
 * `navigator` e `BroadcastChannel`. A separacao existe para que `clearUserScopedStorage` — o
 * requisito bloqueante PWA-033/PWA-034 — seja testavel sem navegador; aqui mora o que so pode
 * ser verificado no e2e.
 *
 * Cada porta **engole a ausencia da API**, e nao a presenca de erro: `BroadcastChannel` nao
 * existe em Safari antigo, `caches` nao existe em contexto inseguro, `pushManager` nao existe
 * sem service worker. Uma purga que lancasse `ReferenceError` no primeiro navegador exotico
 * deixaria dado do titular anterior no aparelho — e a `purga.ts` ja trata erro, mas nao deve
 * precisar tratar "esta API nao existe aqui".
 */

import type { ApiClient } from '@farmacia/api-client';
import { openDB } from 'idb';

import { NOME_DO_BANCO, STORES, VERSAO_DO_BANCO, type NomeDeStore } from './esquema.js';
import type { MotivoDaPurga, PortasDePurga } from './purga.js';

/** Abre (e migra) o banco `fs-app`. `PWA §7.2`: migracao incremental no `upgrade`. */
export async function abrirBanco() {
  return await openDB(NOME_DO_BANCO, VERSAO_DO_BANCO, {
    upgrade(banco) {
      for (const definicao of STORES) {
        if (banco.objectStoreNames.contains(definicao.nome)) continue;
        const store = banco.createObjectStore(definicao.nome, { keyPath: definicao.keyPath });
        for (const indice of definicao.indices) {
          store.createIndex(indice.nome, indice.keyPath);
        }
      }
    },
  });
}

/** `PWA §7.5`, passo 5 — o canal que avisa as outras abas. */
export const CANAL_DE_AUTENTICACAO = 'auth';

export function portasDePurgaDoNavegador(cliente: ApiClient): PortasDePurga {
  return {
    async limparStore(store: NomeDeStore) {
      if (typeof indexedDB === 'undefined') return;
      const banco = await abrirBanco();
      if (!banco.objectStoreNames.contains(store)) return;
      await banco.clear(store);
    },

    async listarCaches() {
      if (typeof caches === 'undefined') return [];
      return await caches.keys();
    },

    async apagarCache(nome: string) {
      if (typeof caches === 'undefined') return;
      await caches.delete(nome);
    },

    async desinscreverPush() {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
      const registro = await navigator.serviceWorker.getRegistration();
      const inscricao = await registro?.pushManager.getSubscription();
      if (inscricao === null || inscricao === undefined) return null;
      const endpoint = inscricao.endpoint;
      await inscricao.unsubscribe();
      return endpoint;
    },

    avisarServidorDoPush(endpoint: string): Promise<void> {
      // DUVIDA: `DELETE /v1/me/push-subscriptions/{id}` e rota **prevista** (`API §6.18`) e nao
      // existe no `@farmacia/api-client`. Enquanto isso, o `unsubscribe()` local ja resolve o
      // vazamento entre usuarios do mesmo aparelho (PWA-033) — o servidor descobre o endpoint
      // morto pelo `404`/`410` do servico de push (PWA-050). O que falta e a limpeza imediata.
      void endpoint;
      void cliente;
      return Promise.resolve();
    },

    avisarOutrasAbas() {
      if (typeof BroadcastChannel === 'undefined') return;
      const canal = new BroadcastChannel(CANAL_DE_AUTENTICACAO);
      canal.postMessage({ type: 'LOGGED_OUT' });
      canal.close();
    },

    avisarServiceWorker(mensagem: { readonly type: string; readonly reason: MotivoDaPurga }) {
      if (typeof navigator === 'undefined') return;
      if (!('serviceWorker' in navigator)) return;
      navigator.serviceWorker.controller?.postMessage(mensagem);
    },

    localStorage: typeof localStorage === 'undefined' ? null : localStorage,

    limparEstadoDoClienteHttp() {
      cliente.resetSessionState();
    },
  };
}
